import React, { useState } from 'react';
import AuthService from '../services/AuthService';

function LoginView({ onLogin }) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const extractLocalDigits = (value = '') => {
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return digitsOnly.slice(1);
    }
    return digitsOnly;
  };

  const validateAndFormatPhoneNumber = (value) => {
    const localDigits = extractLocalDigits(value);
    if (localDigits.length !== 10) {
      return { error: 'Phone number must contain exactly 10 digits.' };
    }
    return { formatted: `+1${localDigits}` };
  };

  const sendVerificationCode = async () => {
    setLoading(true);
    setError('');

    try {
      const { formatted, error: validationError } = validateAndFormatPhoneNumber(phoneNumber);
      if (validationError) {
        setError(validationError);
        setLoading(false);
        return;
      }

      setPhoneNumber(formatted);
      await AuthService.sendVerificationCode(formatted, smsConsent);
      setCodeSent(true);
    } catch (error) {
      setError(error.message);
    }

    setLoading(false);
  };

  const verifyCode = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await AuthService.login(phoneNumber, verificationCode);
      await onLogin(data);
    } catch (error) {
      setError(error.message);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
      <div className="bg-gray-800 p-8 rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex justify-center mb-6">
          <img
            src="/brand/lettermark-darkmode@3x.png"
            alt="Family Ring"
            style={{ height: 73, width: 'auto' }}
          />
        </div>

        {error && (
          <div className="bg-red-600 text-white p-3 rounded mb-4">
            {error}
          </div>
        )}

        {!codeSent ? (
          <div className="space-y-4">
            <input
              type="tel"
              placeholder="Phone Number (e.g., 5551234567)"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full p-3 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 ring-[#5D2FFF]"
            />
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#5D2FFF] rounded"
              />
              <span className="text-sm text-gray-400">
                I consent to receive SMS verification codes from Family Ring.
              </span>
            </label>
            <button
              onClick={sendVerificationCode}
              disabled={!phoneNumber || !smsConsent || loading}
              className="w-full p-3 bg-[#5D2FFF] hover:bg-[#8360FF] rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Sending...' : 'Send Verification Code'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <input
              type="text"
              placeholder="Enter Verification Code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              className="w-full p-3 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 ring-[#5D2FFF]"
            />
            <button
              onClick={verifyCode}
              disabled={!verificationCode || loading}
              className="w-full p-3 bg-[#5D2FFF] hover:bg-[#8360FF] rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
            <button
              onClick={() => setCodeSent(false)}
              className="w-full p-2 text-gray-400 hover:text-white"
            >
              Change Phone Number
            </button>
          </div>
        )}
        <div className="mt-8 pt-6 border-t border-gray-700 text-center">
          <p className="text-sm text-gray-400 mb-4">Don't have an account? Get the app to sign up.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://apps.apple.com/us/app/family-ring/id6504144806"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 bg-black hover:bg-gray-900 text-white px-4 py-2.5 rounded-xl transition-colors"
            >
              <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-300">Download on the</div>
                <div className="text-sm font-semibold leading-tight">App Store</div>
              </div>
            </a>
            <a
              href="https://play.google.com/store"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 bg-black/50 text-gray-400 px-4 py-2.5 rounded-xl cursor-default pointer-events-none"
            >
              <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.18 23.77c-.35-.17-.59-.5-.59-.91V1.14c0-.42.24-.74.59-.91l11.42 11.77L3.18 23.77zM15.72 12L5.09 1.37l12.59 7.24L15.72 12zm0 0l1.96-3.37L5.09 22.63 15.72 12zm2.13-3.54l3.36 1.93c.5.29.5.93 0 1.22l-3.36 1.93L15.72 12l2.13-3.54z"/>
              </svg>
              <div>
                <div className="text-[10px] uppercase tracking-wide">Android</div>
                <div className="text-sm font-semibold leading-tight">Coming Soon</div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginView;
